/**
 * Reserved handles that cannot be used by artists.
 * These are protected to prevent conflicts with app routes and common paths.
 */
export const RESERVED_HANDLES = new Set([
  // App routes
  'login',
  'logout',
  'signout',
  'signin',
  'signup',
  'register',
  'onboarding',
  'dashboard',
  'api',
  'terms',
  'privacy',
  'select-artist',
  'select',
  'create-artist',
  'v',
  'venue',
  'venues',
  'join-venue',
  'guest-book-complete',
  'referrals',
  'how-it-works',
  'interactive-demo',
  'embed',
  
  // Common reserved paths
  'admin',
  'administrator',
  'help',
  'support',
  'about',
  'contact',
  'settings',
  'profile',
  'account',
  'user',
  'users',
  'artist',
  'artists',
  'event',
  'events',
  'live',
  'search',
  'explore',
  'discover',
  'home',
  'index',
  'blog',
  'news',
  'faq',
  'pricing',
  'features',
  'analytics',
  'songs',
  
  // Brand protection
  'iperform',
  'iperformlive',
  'i-perform',
  'i-perform-live',
  
  // Technical paths
  'static',
  'assets',
  'images',
  'uploads',
  'files',
  'media',
  'public',
  'private',
  'auth',
  'oauth',
  'callback',
  'webhooks',
  'webhook',
  'invite',
  'invitation',
  'invitations',
  'team',
  'join',
  'accept',
  '_next',
  '_error',
  '404',
  '500',
  
  // Common abuse prevention
  'www',
  'mail',
  'email',
  'ftp',
  'ssh',
  'root',
  'null',
  'undefined',
  'test',
  'demo',
  'example',
])

/**
 * Check if a handle is reserved
 */
export function isReservedHandle(handle: string): boolean {
  return RESERVED_HANDLES.has(handle.toLowerCase())
}

/**
 * Regex pattern for allowed handle characters.
 * Used for both validation and input sanitization.
 * Allowed: lowercase letters, numbers, hyphens, periods, and underscores.
 */
export const HANDLE_ALLOWED_CHARS_REGEX = /^[a-z0-9._-]+$/

/**
 * Sanitize a handle input by removing disallowed characters.
 * Use this for onChange handlers to filter user input.
 */
export function sanitizeHandle(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9._-]/g, '')
}

/**
 * Validate a handle and return an error message if invalid
 * 
 * Allowed characters: lowercase letters, numbers, hyphens, periods, and underscores.
 * - Can start with underscore (_) but NOT with period (.)
 * - Cannot have consecutive special characters (.., __, --, etc.)
 * - Periods and underscores are safe in URLs - e.g., "iperform.live/_demo.artist"
 */
export function validateHandle(handle: string): { valid: boolean; error?: string } {
  // Check format first - allow lowercase letters, numbers, hyphens, periods, and underscores
  if (!HANDLE_ALLOWED_CHARS_REGEX.test(handle)) {
    return { 
      valid: false, 
      error: 'Handle can only contain lowercase letters, numbers, hyphens, periods, and underscores' 
    }
  }
  
  if (handle.length < 3) {
    return { valid: false, error: 'Handle must be at least 3 characters' }
  }
  
  if (handle.length > 50) {
    return { valid: false, error: 'Handle must be 50 characters or less' }
  }
  
  // Check start/end characters
  if (handle.startsWith('-') || handle.endsWith('-')) {
    return { valid: false, error: 'Handle cannot start or end with a hyphen' }
  }
  
  // Can start with underscore, but not period
  if (handle.startsWith('.')) {
    return { valid: false, error: 'Handle cannot start with a period' }
  }
  
  if (handle.endsWith('.')) {
    return { valid: false, error: 'Handle cannot end with a period' }
  }
  
  // Check consecutive special characters (no repeating ., _, or -)
  if (/[._-]{2}/.test(handle)) {
    return { valid: false, error: 'Handle cannot contain consecutive special characters (., _, or -)' }
  }
  
  // Check reserved handles
  if (isReservedHandle(handle)) {
    return { valid: false, error: 'This handle is reserved and cannot be used' }
  }
  
  return { valid: true }
}

